import Grid from '@material-ui/core/Grid';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Theme,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Popover from '@material-ui/core/Popover';
import { convertFromRaw, Editor, EditorState } from 'draft-js';
import React, { useEffect, useState } from 'react';

import PostListItemMenu from './post-list-item-menu';

import { baseTheme } from '../../../theme';
import { IHighLight } from '../../../services/interfaces';
import { getPostsHighlights } from '../../../store/posts/actions';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      borderTop: 'solid rgba(0, 0, 0, .2) 1px',
    },
    static: {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: 'rgba(120, 120, 120, .08)',
      },
    },
    public: {
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: 'rgba(65, 0, 153, .08)',
      },
    },
    private: {
      color: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: 'rgba(0, 101, 207, .08)',
      },
    },
    selectedStatic: {
      backgroundColor: 'rgba(120, 120, 120, .08)',
    },
    selectedPublic: {
      backgroundColor: 'rgba(65, 0, 153, .08)',
    },
    selectedPrivate: {
      backgroundColor: 'rgba(0, 101, 207, .08)',
    },
    staticButtonColor: {
      color: theme.palette.text.primary,

      '&:hover': {
        color: theme.palette.text.secondary,
      },
    },
    heading: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    group: {
      fontWeight: 'bold',
    },
    postTitle: {
      fontSize: '1.4em',
      padding: theme.spacing(0),
      margin: theme.spacing(0),
    },
    postedIndicator: {
      borderRadius: '10px',
      width: '10px',
      height: '10px',
      backgroundColor: theme.palette.success.main,
      display: 'inline-block',
      marginRight: theme.spacing(1),
    },
    postDate: {
      color: theme.palette.text.primary,
    },
    contextMenu: {
      textAlign: 'right',
      color: theme.palette.text.disabled,
    },
    content: {
      color: theme.palette.text.primary,
      paddingTop: theme.spacing(1),
    },
    contentBody: {
      border: 'solid 1px #cdcdcd',
      paddingBottom: theme.spacing(1),
      minHeight: '18px',
      maxHeight: '70px',
      overflowY: 'scroll',
    },
    highLightBodySpacer: {
      paddingTop: theme.spacing(3),
    },
    highLight: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderBottom: `solid 1px ${theme.palette.text.disabled}`,
    },
    spanspacer: {
      display: 'inline-block',
    },
    preveiwImage: {
      width: '100px',
      paddingRight: theme.spacing(1),
    },
    publicColor: {
      color: theme.palette.primary.main,
    },
    privateColor: {
      color: theme.palette.secondary.main,
    },
    staticColor: {
      color: theme.palette.text.primary,
    },
  }),
);

export default function PostListItem(props: any) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const classes = useStyles(baseTheme);
  const dispatch = useDispatch();
  const publicOrPrivate = props.public
    ? classes.public
    : !props.group?.id
      ? classes.static
      : classes.private;
  const storedState = EditorState.createWithContent(convertFromRaw(JSON.parse(props.content)));
  const isPosted = props.posted
    ? <span className={classes.spanspacer}>
      <span className={classes.postedIndicator}></span>
    </span>
    : null;
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  useEffect(() => {
    if (!isLoaded) {
      getPostsHighlights(props.id, dispatch);
      setIsLoaded(true);
    }
  }, [dispatch, props.id, isLoaded]);

  const setBackgroundSelected = () => {
    if (props.selected && props.public) {
      return classes.selectedPublic;
    }

    if (props.selected && !props.group?.id) {
      return classes.selectedStatic;
    }

    if (props.selected) {
      return classes.selectedPrivate;
    }

    return null;
  };

  const renderHighlightImage = (image: string | undefined) => {
    return image ? (<img
      src={image}
      alt="preview"
      className={classes.preveiwImage}
    />) : <div className={classes.preveiwImage}>&nbsp;</div>;
  };

  const sorter = (a: any, b: any) => {
    if (a.order < b.order) {
      return -1;
    }

    if (a.order > b.order) {
      return 1;
    }

    return 0;
  };

  const renderHighlights = (highlights: IHighLight[]) =>
    highlights ?
      highlights.sort(sorter).map((highlight: IHighLight) => (
        <Grid container item xs={12} key={highlight.id} className={classes.highLight}>
          <Grid>
            {renderHighlightImage(highlight.image)}
          </Grid>
          <Grid xs={10}>
            {highlight.content}
          </Grid>
        </Grid>
      ))
      : null;

  const editPost = () => {
    props.selectPost(props.id);
  };

  const deletePost = (event: any) => {
    event.stopPropagation();
    setDeleteConfirmation(false);
    props.deletePost(props.id);
  };

  const onHoverOverMenu = (event: any) => {
    props.setAllowEditPost(false);
  };
  const onHoverOutMenu = (event: any) => {
    props.setAllowEditPost(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    props.setAllowEditPost(true);
  };

  const handleConfirmDeleteClose = (event: any) => {
    event.stopPropagation();
    console.log(event);
    setDeleteConfirmation(false);
  };

  const handleEditPostClick = (event: any) => {
    if (props.allowEditPost) {
      editPost();
    }
  };

  const handleMenuPostClick = (event: any) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const confirmDelete = () => {
    setDeleteConfirmation(true);
  };

  const colorSelector = props.group.type === 'Public'
    ? classes.publicColor
    : props.group.type === 'Closed'
      ? classes.privateColor
      : classes.staticColor;
  const iconColor = props.group.type === 'Public'
    ? 'primary'
    : props.group.type === 'Closed'
      ? 'secondary'
      : undefined;
  const moreVertIconColor = !(props.group.type === 'Public' || props.group.type === 'Closed') ?
      'action' : undefined;
  return (
    <Grid
      item
      container
      direction="column"
      className={`${classes.root} ${publicOrPrivate} ${setBackgroundSelected()}`}
      onClick={handleEditPostClick}
    >
      <Grid item container className={classes.heading}>
        <Grid
          item
          container
          direction="column"
          xs={11}
        >
          <Grid item className={classes.group}>
            {props.groupName}
          </Grid>

          <Grid item>
            <h4 className={classes.postTitle}>
              {isPosted}
              {props.title}
            </h4>
          </Grid>

          <Grid item className={classes.postDate}>
            {props.date}
          </Grid>
        </Grid>

        <Grid item xs={1} className={classes.contextMenu}>
            <IconButton
                color={iconColor}
                onClick={handleMenuPostClick}
                onMouseOver={onHoverOverMenu}
                onMouseOut={onHoverOutMenu}
            >
                <MoreVertIcon color={moreVertIconColor} />
            </IconButton>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            onExited={handleClose}
            onMouseOver={onHoverOverMenu}
            onMouseOut={onHoverOutMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <PostListItemMenu
              anchorEl={anchorEl}
              id={id}
              post={props.post}
              posted={props.posted}
              groupType={props.group.type}
              handleClose={handleClose}
              handleEdit={editPost}
              handleDelete={confirmDelete} />
          </Popover>
        </Grid>
      </Grid>

      <Grid item className={classes.content}>
        <Grid item className={classes.contentBody}>
          <Editor
            editorState={storedState}
            readOnly={true}
            onChange={() => { }}
          />
        </Grid>
        <div className={classes.highLightBodySpacer}></div>
        <b>Highlights:</b>
        {renderHighlights(props.highlights)}
      </Grid>

      <Dialog
        open={deleteConfirmation}
        onClose={handleConfirmDeleteClose}
      >
        <DialogTitle id="alert-delete-dialog">{`Delete ${props.title}`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to delete {props.title}?
          </DialogContentText>
          <DialogActions>
            <Button
              onClick={handleConfirmDeleteClose}
              className={classes.staticButtonColor}>Disagree</Button>
            <Button onClick={deletePost} className={colorSelector}>Agree</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
